import { AsyncPipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, Injector, OnInit, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FileFolderNode } from '@r1-compass-library/components/file-tree/file-tree.component';
import { HeaderComponent, Page } from '@r1-compass-library/components/header/header.component';
import { SidenavComponent, SidenavLink } from '@r1-compass-library/components/sidenav/sidenav.component';
import { NavigationService } from '@r1-compass-library/services';
import { RoutingService } from '@r1-compass-library/services/routing.service';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { GlobalUsersStore } from '@r1-compass-library/store/users/globalUsersStore';
import { Observable, of, switchMap } from 'rxjs';
import { handleBrowserStorageAndMakeLogoutRoute } from '../app.routes';
import { AppConstants } from './app.constants';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { SidenavValidationRegistry } from '@r1-compass-library/features/validation-sync';

@Component({
    selector: 'r1-compass-app-compass',
    template: `
        <r1-compass-lib-header
            [pages]="pagesWithDivisionId()"
            [selectedDivisionCode]="loggedInUserStore.selectedIamDivision()?.divisionCode ?? 'Division Loading...'"
            [divisionChoices]="loggedInUserStore.usersAvailableDivisions()"
            [currentEntityHomepage]="currentEntityHomepage()"
            (switchDivision)="switchDivision()"
            (logOut)="onLogout()" />
        <r1-compass-lib-sidenav
            [showSidenav]="showSidenav()"
            [links]="links()"
            [sidenavMainTitle]="sidenavTitles()"
            [divisionSettingsDisabled]="divisionSettingsDisabled()"
            [sidenavFolders]="(sidenavFolders$ | async) ?? []">
            <div id="page">
                <router-outlet />
            </div>
            <!-- <div>loading spinner is placed in here</div> -->
        </r1-compass-lib-sidenav>
    `,
    styles: `
        #page {
            margin: 1rem;
        }
    `,
    imports: [SidenavComponent, RouterModule, HeaderComponent, AsyncPipe],
    host: {
        '(window:beforeunload)': 'onWindowBeforeUnload($event)',
    },
})
export class CompassComponent implements OnInit {
    readonly #navigationService = inject(NavigationService);
    readonly #destroyRef = inject(DestroyRef);
    protected readonly routingService = inject(RoutingService);
    protected readonly route = inject(ActivatedRoute);
    readonly #router = inject(Router);
    protected readonly loggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #usersStore = inject(GlobalUsersStore);
    readonly #injector = inject(Injector);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #globalDivisionsStore = inject(GlobalDivisionsStore);
    readonly #sidenavRegistry = inject(SidenavValidationRegistry);

    private divisionId: Signal<string | undefined> = computed(() =>
        this.loggedInUserStore.selectedIamDivisionId()?.toString()
    );

    ngOnInit() {
        this.#usersStore.getAllRoles();
    }

    /**
     * @description Prevents manual user URL change or page reload with unsaved changes. Angular route guard cannot pick those up.
     * This pops the generic browser warning about unsaved changes. Eloqua has this implemented but with their own custom messaging,
     * so we may look into this later, but an Angular SPA may not work the same.
     *
     * Used in the component's `:host {...}` binding above
     **/
    onWindowBeforeUnload($event: BeforeUnloadEvent): void {
        if (this.#formSaveWarningStore.canDeactivate$.getValue().dirtied) {
            $event.preventDefault();
        }
    }

    protected switchDivision() {
        this.#router.navigateByUrl('/switch-division');
    }

    private pages = signal(AppConstants.pages).asReadonly();
    protected pagesWithDivisionId: Signal<Page[]> = computed(() => {
        const divisionEntry = this.pages().find(page => page.name === 'Division Settings');
        const replacedWithDivisionNumber: Page | undefined = {
            ...divisionEntry!,
            url: divisionEntry?.url.replace(':id', this.divisionId() ?? '') ?? '',
        };

        return this.pages().map(page => {
            return page.name === 'Division Settings' ? replacedWithDivisionNumber : page;
        });
    });

    protected currentEntityHomepage: Signal<Page> = computed(
        () =>
            this.pagesWithDivisionId().find(page =>
                `/${page.url}`.startsWith(`${this.routingService.topRoute()}/`)
            ) ?? {
                name: 'Login',
                url: 'login',
                rootFolderId: '',
                adminOnly: false,
            }
    );

    showSidenav: Signal<boolean> = computed(() => {
        switch (this.routingService.topRoute()) {
            case `/${AppConstants.ReportingComponent.path}`:
                return false;
            default:
                return true;
        }
    });

    sidenavTitles: Signal<string> = computed(() => {
        switch (this.routingService.topRoute()) {
            case `/${AppConstants.PlaysComponent.path}`:
                return AppConstants.PlaysComponent.mainTitle;
            case `/${AppConstants.LifecycleRulesComponent.path}`:
                return AppConstants.LifecycleRulesComponent.mainTitle;
            case `/${AppConstants.TemplateCriteriaComponent.path}`:
                return AppConstants.TemplateCriteriaComponent.mainTitle;
            case `/${AppConstants.ContentLibraryComponent.path}`:
                return AppConstants.ContentLibraryComponent.mainTitle;
            case `/${AppConstants.DivisionSettingsComponent.path}`:
                return AppConstants.DivisionSettingsComponent.mainTitle;
            case `/${AppConstants.AdminSettingsComponent.path}`:
                return AppConstants.AdminSettingsComponent.mainTitle;
            default:
                return 'Compass';
        }
    });

    sidenavFolders$: Observable<FileFolderNode[]> = this.routingService.currentRoute$.pipe(
        switchMap(() => {
            const routeWithoutParams = this.routingService.currentRoutePieces().routeWithoutParams;

            switch (routeWithoutParams) {
                case `/${AppConstants.PlaysComponent.path}`:
                    return this.#navigationService.getSidenavFolders('plays');
                case `/${AppConstants.TemplateCriteriaComponent.path}`:
                    return this.#navigationService.getSidenavFolders('template-criteria');
                case `/${AppConstants.LifecycleRulesComponent.path}`:
                    return this.#navigationService.getSidenavFolders('lifecycle-rules');
                case `/${AppConstants.ContentLibraryComponent.path}`:
                    return this.#navigationService.getSidenavFolders('dynamic-content');
                default:
                    return of([]);
            }
        }),
        takeUntilDestroyed(this.#destroyRef)
    );

    protected links = computed<SidenavLink[]>(() => {
        const hasParams = !!this.routingService.currentRoutePieces().routeParams;
        const hideSidenavLinks = this.routingService.hideSidenavLinks();
        switch (this.routingService.topRoute()) {
            case `/${AppConstants.PlaysComponent.path}`:
                return hideSidenavLinks
                    ? []
                    : this.setEntityLinksWithValidation(
                          this.#sidenavRegistry.getInvalidSections('plays'),
                          AppConstants.playLinks
                      );
            case `/${AppConstants.LifecycleRulesComponent.path}`: {
                return hideSidenavLinks
                    ? []
                    : this.setEntityLinksWithValidation(
                          this.#sidenavRegistry.getInvalidSections('lifecycle-rules'),
                          AppConstants.lifecycleLinks
                      );
            }
            case `/${AppConstants.TemplateCriteriaComponent.path}`: {
                return hideSidenavLinks
                    ? []
                    : this.setEntityLinksWithValidation(
                          this.#sidenavRegistry.getInvalidSections('template-criteria'),
                          AppConstants.templateCriteriaLinks
                      );
            }
            case `/${AppConstants.ContentLibraryComponent.path}`: {
                return hideSidenavLinks
                    ? []
                    : this.setEntityLinksWithValidation(
                          this.#sidenavRegistry.getInvalidSections('content-library'),
                          AppConstants.contentLibraryLinks
                      );
            }
            case `/${AppConstants.DivisionSettingsComponent.path}`:
                return hasParams
                    ? []
                    : this.routingService.addAssetIdToLinks(
                          AppConstants.divisionLinks.map(link => {
                              return {
                                  ...link,
                                  url: link.url.replace(/^/, `/${AppConstants.DivisionSettingsComponent.path}/:id/`),
                              };
                          })
                      );
            case `/${AppConstants.AdminSettingsComponent.path}`:
                return AppConstants.adminLinks;
            default:
                return [];
        }
    });

    divisionSettingsDisabled = computed(() => !this.#globalDivisionsStore.allDivisionConfigs()[0].config?.mapping);

    onLogout() {
        this.#router.navigateByUrl(handleBrowserStorageAndMakeLogoutRoute(this.#injector, false));
    }

    private setEntityLinksWithValidation(invalidSections: string[], entityLinks: SidenavLink[]) {
        return this.routingService.addAssetIdToLinks(
            entityLinks.map(link => ({
                ...link,
                hasValidationIssue: invalidSections.includes(link.sectionKey ?? link.url),
            }))
        );
    }
}
